import { useEffect, useRef, useState } from 'react';

import { ApiError } from '../api/client';
import { ownerApi } from '../api/owner';
import { QuotaBanner } from '../components/QuotaBanner';
import { SuspendedBanner } from '../components/SuspendedBanner';
import type { OwnerBusiness, OwnerMediaItem, QuotaStatus } from '../types';

export function GalleryManager() {
  const [business, setBusiness] = useState<OwnerBusiness | null>(null);
  const [quota, setQuota] = useState<QuotaStatus | null>(null);
  const [media, setMedia] = useState<OwnerMediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reload = async () => {
    const [b, q, m] = await Promise.all([ownerApi.getBusiness(), ownerApi.getQuota(), ownerApi.listMedia()]);
    setBusiness(b);
    setQuota(q);
    setMedia(m.data);
  };

  useEffect(() => {
    reload().finally(() => setLoading(false));
  }, []);

  const handleFileSelected = async (file: File) => {
    setUploading(true);
    setMessage(null);
    try {
      await ownerApi.uploadMedia(file, 'gallery');
      await reload();
      setMessage({ type: 'success', text: 'Image uploaded.' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setMessage({ type: 'error', text: 'Monthly edit limit reached.' });
      } else {
        setMessage({ type: 'error', text: 'Upload failed.' });
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await ownerApi.deleteMedia(id);
      await reload();
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setMessage({ type: 'error', text: 'Monthly edit limit reached.' });
      }
    }
  };

  if (loading) return <p className="text-sm text-slate-500">Loading…</p>;

  const disabled = business?.is_suspended || (quota ? quota.remaining <= 0 : false);

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-slate-900">Gallery</h1>

      {business?.is_suspended && <SuspendedBanner />}
      {quota && <QuotaBanner quota={quota} />}

      <div className="rounded-brand border border-slate-200 bg-white p-6">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          disabled={disabled || uploading}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFileSelected(file);
          }}
          className="text-sm"
        />
        {uploading && <p className="mt-2 text-sm text-slate-500">Uploading…</p>}
        {message && (
          <p className={`mt-2 text-sm ${message.type === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>
            {message.text}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {media.map((item) => (
          <div key={item.id} className="group relative overflow-hidden rounded-brand border border-slate-200">
            <img src={item.thumbnail_url ?? item.url} alt="" className="aspect-square w-full object-cover" />
            <button
              onClick={() => handleDelete(item.id)}
              disabled={disabled}
              className="absolute right-2 top-2 rounded-full bg-black/60 px-2 py-1 text-xs text-white opacity-0 transition group-hover:opacity-100"
            >
              Delete
            </button>
          </div>
        ))}
        {!media.length && <p className="text-sm text-slate-500">Koi images nahi hai abhi.</p>}
      </div>
    </div>
  );
}
